import { FlyoutContextOutgoingProps, withFlyoutContext } from "@app/shared/Flyouts";
import { PureComponent, JSX } from "react";
import { SpecialtyGroupModel } from "./specialty";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { LoadingSpinner, Page, PageHeader } from "@app/shared";
import { ModalLink } from "@app/shared/Modals";

interface CreatePanelProps {
    currentUserid: number;
    selectedRow: SpecialtyGroupModel;
    setIsEdited: React.Dispatch<React.SetStateAction<boolean>>;
}

interface CreatePanelState {
    loading: boolean;
    specialtyRollUpProperties: SpecialtyRollUpItem[];
    specialtyGroupTableData: SpecialtyGroupTableData[];
}

interface SpecialtyRollUpItem {
    code: string;
    name: string;
    displayOrder: number;
    weight: string;
}

interface SpecialtyModel {
    code: string;
    name: string;
    displayOrder: number;
    weight: string;
}

interface SpecialtyGroupTableData {
    specialtyCode: string;
    specialtyName: string;
    [wafflesKeyColumnName]: string;
    reorder: JSX.Element;
}

const specialtyRollupDropdownOptions: { key: string, name: string }[] =
    [
        {
            key: "moveUp",
            name: "Move Up",
        },
        {
            key: "moveDown",
            name: "Move Down",
        },
        {
            key: "moveTop",
            name: "Move To Top",
        },
        {
            key: "moveBottom",
            name: "Move To Bottom",
        },
        {
            key: "delete",
            name: "Delete Specialty",
        }
    ];

class SpecialtyRollupFlyoutContent extends PureComponent<CreatePanelProps & FlyoutContextOutgoingProps, CreatePanelState> {
    constructor(props: CreatePanelProps & FlyoutContextOutgoingProps) {
        super(props);
        const specialtyRollUpLineItems: SpecialtyRollUpItem[] = props.selectedRow.specialties;
        if (props.selectedRow.specialties.every((x) => (x.displayOrder === 1 || x.displayOrder === 0))) {
            specialtyRollUpLineItems.forEach(element => {
                element.displayOrder = specialtyRollUpLineItems.indexOf(element) +1;
            });
        } else {
            specialtyRollUpLineItems.map((x) => (
                {
                    code: x.code,
                    displayOrder: x.displayOrder,
                    name: x.name,
                    weight: x.weight
                }
            ))
        }
        this.state = {
            loading: false,
            specialtyGroupTableData: [],
            specialtyRollUpProperties: specialtyRollUpLineItems
        };
    }
    /* eslint-enable sort-keys */

    public async componentDidMount() {
        this.setState({ loading: true });

        await this.createSpecialityRollUpWafflesTable(this.state.specialtyRollUpProperties);

        this.setState({
            loading: false,
        });
    }

    private DisplayReorder = (row: SpecialtyModel) => {
        return specialtyRollupDropdownOptions.map((x) =>
            <NewActionMenuItem
                display={x.name}
                key={x.key}
                isSelected={false}
                onClick={() => {
                    this.handleDisplayReorder(row, x.name);
                    // this is done to close the flyout dropdown since by default after selecting an option it remained open
                    $('[data-testId="new-action-menu__hideme-button"]').parent().trigger("click");
                }}
                testId="manage-specialty__download_template-option--json"
            />
        )
    }

    private handleDisplayReorder = (row: SpecialtyModel, operation: string) => {
        const specialtyRow = this.state.specialtyRollUpProperties;
        if (operation === 'Move Up' && (row.displayOrder !== 1)) {
            const rowToChange = specialtyRow.find((e) => e.displayOrder === (row.displayOrder - 1));
            rowToChange.displayOrder = row.displayOrder;
            row.displayOrder = row.displayOrder - 1;
        }
        if (operation === 'Move Down' && (row.displayOrder !== specialtyRow.length)) {
            const rowToChange = specialtyRow.find((e) => e.displayOrder === (row.displayOrder + 1));
            rowToChange.displayOrder = row.displayOrder;
            row.displayOrder = row.displayOrder + 1;
        }
        if (operation === 'Move To Top' && (row.displayOrder !== 1)) {
            specialtyRow.forEach(element => {
                if (element.displayOrder < row.displayOrder) {
                    element.displayOrder = element.displayOrder + 1;
                }
            });
            row.displayOrder = 1;
        }
        if (operation === 'Move To Bottom' && (row.displayOrder !== specialtyRow.length)) {
            specialtyRow.forEach(element => {
                if (element.displayOrder > row.displayOrder) {
                    element.displayOrder = element.displayOrder - 1;
                }
            });
            row.displayOrder = specialtyRow.length;
        }
        if (operation === 'Delete Specialty') {
            specialtyRow.splice(specialtyRow.findIndex((x) => x.name === row.name), 1)
        }
        this.createSpecialityRollUpWafflesTable(specialtyRow);
        const specialtyModel = this.props.selectedRow;
        specialtyModel.specialties = specialtyRow;
        this.props.setIsEdited(true);
    };

    private createSpecialityRollUpWafflesTable = async (specialty: SpecialtyRollUpItem[]): Promise<SpecialtyGroupTableData[]> => {
        const rowData = specialty.sort((a, b) => a.displayOrder - b.displayOrder).map(rec => {
            /* eslint-disable sort-keys */
            return ({
                key: rec.displayOrder.toString(),
                specialtyName: rec.name,
                specialtyCode: rec.code,
                reorder: (
                    <NewActionMenu
                        testId="hideme"
                        buttonText={'...'}
                        containerClassnames="col-8"
                        buttonClassNames="qes-button-secondary"
                        fixMenu={true}
                        closeOnClick={true}
                        fixedMenuOffset={{
                            x: -600,
                            y: -15
                        }}
                        hideArrow={true}
                    >
                        {this.DisplayReorder(rec)}
                    </NewActionMenu>
                )
            });
        });
        this.setState({ specialtyGroupTableData: rowData });
        return rowData;
    }

    public render = () => {
        const columnConfig: ColumnConfig = {
            specialtyName: {
                displayText: "Specialty Name",
                sizeContentToFit: true,
                testId: "specialtyRollUp-specialtyName",
                noSort: true
            },
            specialtyCode: {
                displayText: "Specialty Code",
                sizeContentToFit: true,
                testId: "specialtyRollUp-specialtycode",
                noSort: true
            },
            reorder: {
                displayText: "Reorder Specialty",
                sizeContentToFit: true,
                testId: "specialtyRollUp-reorder",
                noSort: true
            },
        };

        const handleDeleteSpecialtyRollUp = () => {
            this.props.selectedRow.specialties.splice(0,this.props.selectedRow.specialties.length);
            this.props.setIsEdited(true);
            this.props.handleCloseFlyout();
        }

        return (
            <>
                {this.state.loading ? <LoadingSpinner /> :
                    <Page documentTitle="Specialties Roll-Ups">
                        <PageHeader title={this.props.selectedRow.name} />
                        <ModalLink
                            classNames="qes-button-secondary danger"
                            content={{
                                body: "Are you sure you want to delete this specialty?",
                                cancelButtonText: "Cancel",
                                confirmButtonAction: () => handleDeleteSpecialtyRollUp(),
                                confirmButtonText: "Discard",
                                confirmButtonType: "primary-danger",
                                title: `Delete Specialty`,
                            }}
                            testId="manage-specialty__cancel-delete-modal"
                            hideBodyScroll={true}
                        >
                            Delete
                        </ModalLink>
                        <div className="row mt-3">
                            <div className="col-12" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }} >
                                <div style={{ minWidth: "100%", paddingLeft: "0%", paddingRight: "2%" }}>
                                    <Waffles<SpecialtyGroupTableData>
                                        columns={columnConfig}
                                        data={this.state.specialtyGroupTableData}
                                        enablePagination={false}
                                        fullWidth={true}
                                        loading={this.state.loading}
                                        passThroughSorting={true}
                                        testId="client-manage-specialtyGroup-waffles-table" />
                                </div>
                            </div>
                        </div>
                    </Page>
                }
            </>
        )
    }
}


const SpecialtyRollupFlyout = withFlyoutContext(SpecialtyRollupFlyoutContent);
export default SpecialtyRollupFlyout;